import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, getSessionToken, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Loader2, Upload, Trash2, FileText, BookOpen, CheckCircle2, Save, Layers, Info } from "lucide-react";

/**
 * Admin Program Booklet — upload ONE combined PDF; configure Friday & Saturday page ranges.
 * Backend generates 3 PDFs (full, friday-subset, saturday-subset) + a cover image at upload time.
 * Page ranges can be edited without re-uploading (backend regenerates the subsets).
 */
export default function ProgramLibraryAdmin() {
  const [booklet, setBooklet] = useState(null);
  const [loading, setLoading] = useState(true);
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Editable range specs (bound to the currently-active booklet)
  const [fridaySpec, setFridaySpec] = useState("");
  const [saturdaySpec, setSaturdaySpec] = useState("");
  const [subFri, setSubFri] = useState("");
  const [subSat, setSubSat] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/event/program-booklet");
      setBooklet(data.booklet);
      if (data.booklet) {
        setFridaySpec(data.booklet.friday_pages_spec || "");
        setSaturdaySpec(data.booklet.saturday_pages_spec || "");
        setSubFri(data.booklet.subtitle_friday || "");
        setSubSat(data.booklet.subtitle_saturday || "");
      }
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (!/pdf/i.test(file.type)) { toast.error("Please upload a PDF"); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error("PDF too large (max 50 MB)"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      // If user has already entered custom ranges on this session, honor them
      if (fridaySpec) form.append("friday_pages_spec", fridaySpec);
      if (saturdaySpec) form.append("saturday_pages_spec", saturdaySpec);
      if (subFri) form.append("subtitle_friday", subFri);
      if (subSat) form.append("subtitle_saturday", subSat);
      const res = await fetch(`${API_BASE}/event/admin/program-booklet/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getSessionToken()}` },
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      toast.success("Program booklet uploaded — cover + day subsets generated");
      load();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const savePages = async () => {
    if (!booklet) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/event/admin/program-booklet/${booklet.id}`, {
        friday_pages_spec: fridaySpec,
        saturday_pages_spec: saturdaySpec,
        subtitle_friday: subFri,
        subtitle_saturday: subSat,
      });
      setBooklet(data);
      toast.success("Page ranges saved · day subsets regenerated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!booklet) return;
    if (!window.confirm("Delete the current program booklet? Attendees will see the empty state until a new one is uploaded.")) return;
    try {
      await api.delete(`/event/admin/program-booklet/${booklet.id}`);
      toast.success("Deleted");
      setBooklet(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="mx-auto animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-fuchsia-700" />
            <h3 className="font-[Cormorant_Garamond] text-xl">Program Library</h3>
          </div>
          <p className="text-xs font-[Outfit] text-emerald-800/60">
            Upload the combined 2-day program booklet. The system extracts the cover from page 1 and generates
            separate Friday and Saturday views based on the page ranges you set. Attendees see 3 options in the app:
            Friday view · Saturday view · Combined download.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {!booklet ? (
            <label className="block cursor-pointer rounded-2xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50 p-8 text-center hover:bg-fuchsia-100/60 transition-colors">
              <input
                ref={inputRef}
                type="file"
                accept=".pdf,application/pdf"
                hidden
                onChange={(e) => upload(e.target.files?.[0])}
                data-testid="booklet-upload-input"
              />
              <Upload size={26} className="mx-auto mb-2 text-fuchsia-700" />
              <div className="font-[Outfit] font-bold text-sm text-fuchsia-900">Upload combined 2-day PDF</div>
              <div className="text-[11px] font-[Outfit] text-emerald-800/60 mt-1">
                Max 50 MB · Cover + Friday & Saturday subsets are generated automatically
              </div>
            </label>
          ) : (
            <div className="rounded-2xl bg-white border-2 border-emerald-200 p-4">
              <div className="flex items-start gap-4">
                <img
                  src={`${BACKEND_URL}${booklet.cover_url}`}
                  alt="Cover"
                  className="w-24 h-32 rounded-lg object-cover border border-emerald-200 shadow-sm shrink-0"
                  loading="lazy"
                />
                <div className="min-w-0 flex-1 text-sm font-[Outfit]">
                  <div className="font-bold text-emerald-950">{booklet.filename_original}</div>
                  <div className="text-emerald-800/60 mt-0.5 text-xs">
                    {booklet.page_count} total pages · {(booklet.full_size / (1024 * 1024)).toFixed(1)} MB
                  </div>
                  <div className="text-emerald-800/50 text-[11px] mt-0.5">
                    Uploaded {new Date(booklet.uploaded_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                  </div>
                  <div className="mt-1.5 inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs">
                    <CheckCircle2 size={12} /> Live
                  </div>

                  <div className="mt-3 flex flex-wrap gap-3 text-xs">
                    <a href={`${BACKEND_URL}${booklet.friday_pdf_url}`} target="_blank" rel="noreferrer" className="text-fuchsia-700 font-semibold hover:underline">
                      Friday preview →
                    </a>
                    <a href={`${BACKEND_URL}${booklet.saturday_pdf_url}`} target="_blank" rel="noreferrer" className="text-fuchsia-700 font-semibold hover:underline">
                      Saturday preview →
                    </a>
                    <a href={`${BACKEND_URL}${booklet.full_pdf_url}`} target="_blank" rel="noreferrer" className="text-emerald-700 font-semibold hover:underline">
                      Combined preview →
                    </a>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  onClick={() => inputRef.current?.click()}
                  variant="outline"
                  size="sm"
                  className="rounded-full h-9 text-xs"
                >
                  <Upload size={12} className="mr-1.5" /> Replace PDF
                </Button>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  hidden
                  onChange={(e) => upload(e.target.files?.[0])}
                  data-testid="booklet-replace-input"
                />
                <Button
                  onClick={remove}
                  variant="outline"
                  size="sm"
                  className="rounded-full h-9 text-xs text-fuchsia-700 border-fuchsia-300"
                >
                  <Trash2 size={12} className="mr-1.5" /> Delete
                </Button>
              </div>
              {uploading && (
                <div className="mt-2 flex items-center gap-2 text-xs font-[Outfit] text-emerald-800/70">
                  <Loader2 size={14} className="animate-spin" /> Uploading &amp; extracting…
                </div>
              )}
            </div>
          )}

          {/* Page ranges editor */}
          {booklet && (
            <div className="rounded-2xl border-2 border-emerald-100 bg-emerald-50/40 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Layers size={14} className="text-fuchsia-700" />
                <h4 className="font-[Cormorant_Garamond] text-lg text-emerald-950">Page ranges per day</h4>
              </div>
              <div className="text-xs font-[Outfit] text-emerald-800/70 flex items-start gap-1.5">
                <Info size={12} className="text-emerald-700 shrink-0 mt-0.5" />
                <span>
                  Enter pages as comma/hyphen ranges (e.g. <code className="bg-white px-1.5 py-0.5 rounded text-[11px]">1-6, 7-10, 19-20, 25</code>).
                  Shared pages (1-6, 19-20, 25) appear on both days. Saving regenerates the day subset PDFs immediately.
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700 block mb-1">
                    Friday pages ({(fridaySpec.match(/[\d]+(?=-|,|$)/g) || []).length ? booklet.friday_pages.length : 0} total)
                  </label>
                  <Input
                    data-testid="friday-pages-input"
                    value={fridaySpec}
                    onChange={(e) => setFridaySpec(e.target.value)}
                    placeholder="1-6, 7-10, 19-20, 25"
                    className="font-mono text-sm"
                  />
                  <label className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60 block mt-2 mb-1">
                    Friday subtitle
                  </label>
                  <Input
                    data-testid="friday-subtitle-input"
                    value={subFri}
                    onChange={(e) => setSubFri(e.target.value)}
                    placeholder="Friday · VIP Networking Experience"
                    className="text-sm"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-700 block mb-1">
                    Saturday pages
                  </label>
                  <Input
                    data-testid="saturday-pages-input"
                    value={saturdaySpec}
                    onChange={(e) => setSaturdaySpec(e.target.value)}
                    placeholder="1-6, 11-18, 19-20, 21-24, 25-26"
                    className="font-mono text-sm"
                  />
                  <label className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-emerald-800/60 block mt-2 mb-1">
                    Saturday subtitle
                  </label>
                  <Input
                    data-testid="saturday-subtitle-input"
                    value={subSat}
                    onChange={(e) => setSubSat(e.target.value)}
                    placeholder="Saturday · Business Showers Ceremony"
                    className="text-sm"
                  />
                </div>
              </div>

              <Button
                onClick={savePages}
                disabled={saving}
                data-testid="save-pages-btn"
                className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs h-9"
              >
                {saving ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Save size={12} className="mr-1.5" />}
                Save page ranges (regenerates PDFs)
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
